import React, { FC, useCallback, useMemo } from 'react';
import { Flex } from 'antd';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Form, FormAlerts, FormField } from '../../../common/form';
import { Padding } from '../../../common/components/Padding';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { confirmationHeaders } from '../../../common/utils';
import { useMutationErrors } from '../../../common/queryClient';
import { ActionButton } from '../../../common/components';
import translations from './UserBookmarkModal.translations.json';
import {
  UserBookmarkQueryDtoType,
  UserBookmarkValidFormDataType,
} from './schema/types';
import { UserBookmarkValidFormDataSchema } from './schema/UserBookmarkSchema';
import { NAME_MAX_LENGTH } from '../../../common/constants';
import { useMutateUserBookmarkQuery } from './query/useMutateUserBookmarkQuery';

const { addTranslations, t } = i18nNsFactory('user-bookmark-modal');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export type UserBookmarkModalProps =
  | {
      mode: 'create';
      bookmark?: never;
      excludeUrlParams?: Array<String>;
      includeUrlParams?: Array<{ key: string; value: string }>;
      onClose: () => void;
      onSuccess: () => void;
    }
  | {
      mode: 'edit';
      bookmark: UserBookmarkQueryDtoType;
      excludeUrlParams?: never;
      includeUrlParams?: never;
      onClose: () => void;
      onSuccess: () => void;
    };

export const UserBookmarkModal: FC<UserBookmarkModalProps> = (props) => {
  const {
    bookmark,
    excludeUrlParams = [],
    includeUrlParams = [],
    mode,
    onClose,
    onSuccess,
  } = props;

  const { mutateAsync } = useMutateUserBookmarkQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const handleSubmit = useCallback(
    (values: UserBookmarkValidFormDataType, confirm: boolean) => {
      clearMutationErrors();
      return mutateAsync({
        id: mode === 'edit' ? bookmark?.id : undefined,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(onSuccess)
        .catch((apiErrorResponse) => {
          setMutationErrors(apiErrorResponse.response?.data.errors || []);
        });
    },
    [
      bookmark?.id,
      clearMutationErrors,
      mode,
      mutateAsync,
      onSuccess,
      setMutationErrors,
    ],
  );

  const { pathname, search } = window.location;
  const { title } = document;
  const defaultValues = useMemo(() => {
    let href = pathname;
    const searchParams = new URLSearchParams(search);
    excludeUrlParams.forEach((exclude) =>
      searchParams.delete(exclude as string),
    );
    includeUrlParams.forEach((include) =>
      searchParams.set(include.key, include.value),
    );
    href += searchParams ? `?${searchParams}` : '';

    return {
      name: bookmark?.name ?? title,
      url: bookmark?.url ?? href,
    };
  }, [
    pathname,
    search,
    excludeUrlParams,
    includeUrlParams,
    bookmark?.name,
    bookmark?.url,
    title,
  ]);

  return (
    <Form
      schema={UserBookmarkValidFormDataSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="userFollowedDataObjectDroup"
    >
      <UserBookmarkCreateModalInner mode={mode} onClose={onClose} />
    </Form>
  );
};

function UserBookmarkCreateModalInner({
  mode,
  onClose,
}: {
  mode: string;
  onClose: () => void;
}) {
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      minWidth="20rem"
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`title.` + mode)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="middle">
          <FormField
            name="name"
            label={t('name.label')}
            maxLength={NAME_MAX_LENGTH}
            required
          />
        </Flex>
      </Padding>
    </Modal>
  );
}
